import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Chip, Stack, Tooltip, useMediaQuery, useTheme} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import PlacesAutocompleteInputMui from '../../placesAutocomplete/PlacesAutocompleteInputMui';
import useClickOutsideRef from '../../../hooks/useClickOutsideRef';
import FilterMenuChip from './SearchFilterAndSort/FilterMenuChip';
import SearchMapListToggle from '../SearchMapListToggle';
import {FilterCollapse, PacInputWrapper, SearchBarContainer} from "./styles";
import {PlacePrediction} from "../../placesAutocomplete/interfaces";
import {debounce} from "throttle-debounce";
import {SearchFormContainer} from "./SearchFormContainer";
import {SearchFilterState} from "./SearchFilterAndSort/SearchFilterState";
import {useScrollFilterCollapse} from "../../../hooks/search/useScrollFilterCollapse";
import EquipmentTypes from "../../EquipmentTypes";
import LocationFeatures from "../../LocationFeatures";

const tooltipStyles = {
  tooltip: {
    sx: {
      lineHeight: '1.5em'
    }
  }
}

interface SearchFormProps {
  onSearchSubmit: (pacItem: PlacePrediction | undefined) => void;
  onSearchValueChange?: (value: string) => void;
  onInstantApprovalChange: (isTrue: boolean) => void;
  onLiveLocationsChange: (isTrue: boolean) => void;
  searchValue: string;
  instantApprovalOnly: boolean;
  onlyLiveLocations: boolean;
  showAdminOnlyOptions: boolean;
  filterState: SearchFilterState;
  hideFilterControls?: boolean;
  isFixed?: boolean;
  isMapActive?: boolean;
  /** Shows the List / Map switch beside the search box on small screens. */
  onMapToggle?: () => void;
}

const SearchForm = (props: SearchFormProps) => {
  const {
    onSearchSubmit,
    onSearchValueChange,
    onInstantApprovalChange,
    onLiveLocationsChange,
    searchValue,
    instantApprovalOnly,
    onlyLiveLocations,
    showAdminOnlyOptions,
    filterState,
    hideFilterControls = false,
    isFixed = true,
    isMapActive = false,
    onMapToggle,
  } = props;

  const [selectedPacItem, setSelectedPacItem] = useState<PlacePrediction | undefined>();
  const [showPac, setShowPac] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [filtersTransitioning, setFiltersTransitioning] = useState<boolean>(false);
  const [localSearchValue, setLocalSearchValue] = useState<string>(searchValue);

  const containerRef = useRef<HTMLDivElement>(null);

  // Sync local state when prop changes (e.g., from parent clear)
  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  // Create stable debounced function
  const debouncedOnSearchValueChange = useMemo(
    () => debounce(300, (value: string) => {
      onSearchValueChange?.(value);
    }),
    [onSearchValueChange]
  );

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearchValue(value); // Update immediately for typing
    debouncedOnSearchValueChange(value); // Notify parent after debounce
  };

  const handleSelectPac = (pacItem: PlacePrediction | undefined) => {
    setSelectedPacItem(pacItem);
  };

  const handleClickPac = (pacItem: PlacePrediction) => {
    debouncedOnSearchValueChange.cancel();
    setLocalSearchValue(pacItem.description || '');
    setShowPac(false);
    onSearchSubmit(pacItem);
  };

  const handleCancelIconClick = () => {
    debouncedOnSearchValueChange.cancel();
    setLocalSearchValue('');
    setSelectedPacItem(undefined);
  };

  const handleOnSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPacItem?.place_id) return;
    debouncedOnSearchValueChange.cancel();
    if (selectedPacItem.description) {
      setLocalSearchValue(selectedPacItem.description);
    }
    setShowPac(false);
    onSearchSubmit(selectedPacItem);
  };

  const handleOnFocus = () => {
    setShowPac(true);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const {handleCollapseTransitionEnd} = useScrollFilterCollapse({
    isMobile,
    filtersTransitioning,
    setFiltersTransitioning,
    setShowFilters,
  });

  useClickOutsideRef(containerRef, () => setShowPac(false));

  const {selectedEquipmentType, setSelectedEquipmentType, selectedLocationFeatures, setSelectedLocationFeatures} = filterState;
  // Live only is on by default, so it counts as a filter once an admin turns it off.
  const liveOnlyChanged = showAdminOnlyOptions && !onlyLiveLocations;
  const anyFilterActive = instantApprovalOnly || selectedEquipmentType.length > 0 || selectedLocationFeatures.length > 0 || liveOnlyChanged;
  const handleClearAll = () => {
    onInstantApprovalChange(false);
    if (liveOnlyChanged) onLiveLocationsChange(true);
    setSelectedEquipmentType([]);
    setSelectedLocationFeatures([]);
  };

  const instantApprovalCaption = "Search by locations that offer immediate access to book spaces.";
  const liveLocationsCaption = "Search by locations that are currently live and available to book.";

  return (
    <SearchFormContainer isFixed={isFixed} onSubmit={handleOnSearchSubmit}>
      {/* The ref is on the row, not the search box, so the suggestions stay full width beside the List / Map switch. */}
      <Stack direction="row" alignItems="center" ref={containerRef} sx={{pr: onMapToggle ? {xs: 2, lg: 0} : 0}}>
        <SearchBarContainer container item onFocus={handleOnFocus} sx={{flex: 1, minWidth: 0}}>
          {/* pr: 8px, not 16px, to the List / Map switch, leaving the place name room on a phone. */}
          <PacInputWrapper item sx={onMapToggle ? {pr: {xs: 1, lg: 2}} : undefined}>
            <PlacesAutocompleteInputMui
              onChange={handleSearchInputChange}
              name="pacSearch"
              value={localSearchValue}
              onCancelIconClick={handleCancelIconClick}
              compact
              selectedPacItem={selectedPacItem}
              handleClickPac={handleClickPac}
              handleSelectPac={handleSelectPac}
              containerRef={containerRef}
              showPac={showPac}
              handleKeyUp={handleKeyUp}
            />
          </PacInputWrapper>
        </SearchBarContainer>
        {/* mt: the search box has 4px more padding above it than below, so this centers on the box. */}
        {onMapToggle && <Box sx={{mt: '4px'}}><SearchMapListToggle isMapActive={isMapActive} onToggle={onMapToggle}/></Box>}
      </Stack>
      <FilterCollapse
        in={showFilters}
        onEntered={handleCollapseTransitionEnd}
        onExited={handleCollapseTransitionEnd}
        role="region"
        aria-label="Search filters"
        aria-hidden={!showFilters}
      >
        { !hideFilterControls &&
          // One row that scrolls sideways on small screens, so the map under it keeps its height.
          <Stack direction="row" gap={1} alignItems="center" sx={{
            px: 2,
            pb: 2,
            flexWrap: {xs: 'nowrap', lg: 'wrap'},
            overflowX: {xs: 'auto', lg: 'visible'},
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {display: 'none'},
            '& > *': {flexShrink: 0},
          }}>
            <Tooltip componentsProps={tooltipStyles} arrow title={instantApprovalCaption}>
              <Chip
                icon={<BoltIcon fontSize="small"/>}
                label="Instant approval"
                onClick={() => onInstantApprovalChange(!instantApprovalOnly)}
                color={instantApprovalOnly ? 'primary' : 'default'}
                variant={instantApprovalOnly ? 'filled' : 'outlined'}
                aria-pressed={instantApprovalOnly}
                sx={{fontWeight: 500}}
              />
            </Tooltip>
            <FilterMenuChip label="Equipment" options={EquipmentTypes.OPTIONS_CONDENSED} value={selectedEquipmentType} onChange={setSelectedEquipmentType}/>
            <FilterMenuChip label="Features" options={LocationFeatures.OPTIONS} value={selectedLocationFeatures} onChange={setSelectedLocationFeatures}/>
            {
              showAdminOnlyOptions &&
                <Tooltip componentsProps={tooltipStyles} arrow title={liveLocationsCaption}>
                  <Chip
                    label="Live only"
                    onClick={() => onLiveLocationsChange(!onlyLiveLocations)}
                    color={onlyLiveLocations ? 'primary' : 'default'}
                    variant={onlyLiveLocations ? 'filled' : 'outlined'}
                    aria-pressed={onlyLiveLocations}
                    sx={{fontWeight: 500}}
                  />
                </Tooltip>
            }
            {anyFilterActive && <Button size="small" onClick={handleClearAll}>Clear all</Button>}
          </Stack>
        }
      </FilterCollapse>
    </SearchFormContainer>
  );
};

export default React.memo(SearchForm);

